import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Pause, Play } from 'lucide-react';
import { logMindfulSession } from '../utils/mindful';

/**
 * Each pattern is a self-contained micro-experience: a bespoke SVG/CSS
 * animation that matches the *feeling* of the breath, plus the phase timing
 * and coached instruction text. Everything runs off ONE `t` (seconds since
 * the session started) so scrubbing / pausing is trivial.
 */
const PATTERNS = {
  'box-breath': {
    title: 'Box Breath',
    tagline: '4-4-4-4 · Reset before your next meeting',
    accent: '#173F35',
    rounds: 3,
    phases: [
      { label: 'Breathe in',  seconds: 4 },
      { label: 'Hold',        seconds: 4 },
      { label: 'Breathe out', seconds: 4 },
      { label: 'Hold',        seconds: 4 },
    ],
    Visual: BoxBreathVisual,
  },
  'shoulder-release': {
    title: 'Shoulder Release',
    tagline: '4 in · 6 out · Drop where the day was holding on',
    accent: '#789B83',
    rounds: 5,
    phases: [
      { label: 'Breathe in — lift the shoulders',    seconds: 4 },
      { label: 'Breathe out — drop everything you can', seconds: 6 },
    ],
    Visual: ShoulderReleaseVisual,
  },
  'workday-release': {
    title: 'Workday Release',
    tagline: '4 in · 8 out · Close the laptop cleanly',
    accent: '#D6AD60',
    rounds: 4,
    phases: [
      { label: 'Breathe in',                              seconds: 4 },
      { label: 'Breathe out — let the day ripple away', seconds: 8 },
    ],
    Visual: RippleReleaseVisual,
  },
  'energizer': {
    title: '5-in / 5-out Energizer',
    tagline: 'Even, deep, alive — sharpen without the caffeine',
    accent: '#5B7A65',
    rounds: 6,
    phases: [
      { label: 'Breathe in',  seconds: 5 },
      { label: 'Breathe out', seconds: 5 },
    ],
    Visual: EnergizerVisual,
  },
};

// ---------- Visuals ---------------------------------------------------------

// Box breath: bright dot travels one edge per phase around a square.
function BoxBreathVisual({ phaseIdx, phaseProgress, accent }) {
  // Compute (x, y) of the dot on the square perimeter.
  // Edges: 0 bottom→top-left inhale (LEFT edge), 1 across top hold,
  //        2 top→bottom-right exhale (RIGHT edge), 3 across bottom hold.
  const box = 220;    // outer square size
  const pad = 40;
  const inner = box - pad * 2;
  const p = Math.min(Math.max(phaseProgress, 0), 1);
  const positions = [
    // Left edge: bottom → top
    { x: pad,           y: pad + inner - p * inner },
    // Top edge: left → right
    { x: pad + p * inner, y: pad },
    // Right edge: top → bottom
    { x: pad + inner,   y: pad + p * inner },
    // Bottom edge: right → left
    { x: pad + inner - p * inner, y: pad + inner },
  ];
  const pos = positions[phaseIdx] || positions[0];
  return (
    <svg viewBox={`0 0 ${box} ${box}`} width={box} height={box} aria-hidden>
      <rect
        x={pad} y={pad} width={inner} height={inner}
        fill="none"
        stroke={`${accent}40`}
        strokeWidth={2}
        rx={12}
      />
      <circle cx={pos.x} cy={pos.y} r={12} fill={accent} opacity={0.9}
        style={{ filter: `drop-shadow(0 0 16px ${accent}bb)` }} />
    </svg>
  );
}

// Shoulder release: two "shoulder" arcs at top rise on inhale, drop on exhale.
function ShoulderReleaseVisual({ phaseIdx, phaseProgress, accent }) {
  const size = 260;
  // Inhale (phase 0): shoulders rise from 100 to 60 (Y).
  // Exhale (phase 1): shoulders drop from 60 to 130.
  const p = Math.min(Math.max(phaseProgress, 0), 1);
  const yLift = phaseIdx === 0 ? 100 - p * 40 : 60 + p * 70;
  const openness = phaseIdx === 0 ? 0.55 + p * 0.15 : 0.7 - p * 0.15;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
      {/* Head */}
      <circle cx={size / 2} cy={50} r={22} fill={`${accent}55`} />
      {/* Shoulder arc */}
      <path
        d={`M ${size * 0.15} ${yLift + 20} Q ${size / 2} ${yLift * openness} ${size * 0.85} ${yLift + 20}`}
        fill="none"
        stroke={accent}
        strokeWidth={5}
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 4px 12px ${accent}55)` }}
      />
    </svg>
  );
}

// Workday release: concentric rings emit outward on exhale.
function RippleReleaseVisual({ phaseIdx, phaseProgress, accent }) {
  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  // Inhale: center orb grows softly. Exhale: 3 rings ripple outward.
  const p = Math.min(Math.max(phaseProgress, 0), 1);
  if (phaseIdx === 0) {
    const r = 24 + p * 32;
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
        <circle cx={cx} cy={cy} r={r} fill={accent} opacity={0.85}
          style={{ filter: `drop-shadow(0 0 24px ${accent}aa)` }} />
      </svg>
    );
  }
  // Exhale: 3 offset rings
  const rings = [0, 0.33, 0.66].map((off) => {
    const rp = (p + off) % 1;   // stagger
    const r = 30 + rp * 90;
    const opacity = Math.max(0, 1 - rp) * 0.6;
    return { r, opacity, key: off };
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
      <circle cx={cx} cy={cy} r={24 - p * 6} fill={accent} opacity={0.6} />
      {rings.map((r) => (
        <circle key={r.key} cx={cx} cy={cy} r={r.r} fill="none"
          stroke={accent} strokeWidth={2} opacity={r.opacity} />
      ))}
    </svg>
  );
}

// Energizer: gold orb pulses larger on inhale, smaller on exhale, subtle bob.
function EnergizerVisual({ phaseIdx, phaseProgress, accent }) {
  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  const p = Math.min(Math.max(phaseProgress, 0), 1);
  // Inhale (phase 0): r 30 → 80. Exhale: 80 → 30.
  const r = phaseIdx === 0 ? 30 + p * 50 : 80 - p * 50;
  const bob = Math.sin(p * Math.PI) * 8 * (phaseIdx === 0 ? -1 : 1);
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
      <defs>
        <radialGradient id="orbGrad" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#F5D67D" />
          <stop offset="55%" stopColor="#D6AD60" />
          <stop offset="100%" stopColor="#AA8347" />
        </radialGradient>
      </defs>
      {/* Halo */}
      <circle cx={cx} cy={cy + bob} r={r + 14} fill={`${accent}22`} />
      {/* Orb */}
      <circle cx={cx} cy={cy + bob} r={r} fill="url(#orbGrad)"
        style={{ filter: 'drop-shadow(0 6px 24px rgba(214,173,96,0.55))' }} />
    </svg>
  );
}

// ---------- Page ------------------------------------------------------------

const WorkplaceBreathPage = () => {
  const { pattern } = useParams();
  const navigate = useNavigate();
  const config = PATTERNS[pattern];
  const [running, setRunning] = useState(true);
  const [t, setT] = useState(0);   // seconds elapsed
  const rafRef = useRef();
  const lastRef = useRef(null);

  const totalPhaseSeconds = useMemo(() => {
    if (!config) return 0;
    return config.phases.reduce((s, p) => s + p.seconds, 0);
  }, [config]);
  const totalDuration = totalPhaseSeconds * (config?.rounds || 0);

  // rAF ticker so we don't lock into a coarse setInterval.
  useEffect(() => {
    if (!running || !config) return undefined;
    const tick = (now) => {
      if (lastRef.current == null) lastRef.current = now;
      const dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      setT((prev) => Math.min(prev + dt, totalDuration));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastRef.current = null;
    };
  }, [running, config, totalDuration]);

  // Auto-stop when the session completes + log it to mindful_sessions so it
  // counts toward the user's streak + total mindful minutes.
  const loggedRef = useRef(false);
  useEffect(() => {
    if (config && t >= totalDuration && totalDuration > 0) {
      setRunning(false);
      if (!loggedRef.current) {
        loggedRef.current = true;
        logMindfulSession({
          kind: 'breath',
          durationSec: Math.round(totalDuration),
          ref: `workplace-${pattern}`,
        });
      }
    }
  }, [t, totalDuration, config, pattern]);

  // Reset "already logged" when the user hits Restart so the second run
  // counts too.
  useEffect(() => {
    if (t === 0) loggedRef.current = false;
  }, [t]);

  if (!config) {
    return (
      <div className="p-8 text-center">
        <p className="mb-4">That reset pattern doesn&rsquo;t exist.</p>
        <Link to="/workplace-reset" className="text-[#D6AD60] underline">
          Back to Workplace Reset
        </Link>
      </div>
    );
  }

  // Compute current phase + progress within phase.
  const timeInRound = t % totalPhaseSeconds;
  let acc = 0;
  let phaseIdx = 0;
  let phaseSeconds = config.phases[0].seconds;
  let phaseElapsed = 0;
  for (let i = 0; i < config.phases.length; i += 1) {
    if (timeInRound < acc + config.phases[i].seconds) {
      phaseIdx = i;
      phaseSeconds = config.phases[i].seconds;
      phaseElapsed = timeInRound - acc;
      break;
    }
    acc += config.phases[i].seconds;
  }
  const phaseProgress = phaseSeconds ? phaseElapsed / phaseSeconds : 0;
  const overallProgress = totalDuration ? Math.min(t / totalDuration, 1) : 0;
  const currentRound = Math.min(Math.floor(t / totalPhaseSeconds) + 1, config.rounds);
  const Visual = config.Visual;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: `radial-gradient(circle at 50% 30%, ${config.accent}15 0%, #F7F3EA 60%)`,
      }}
      data-testid={`workplace-breath-${pattern}`}
    >
      {/* Top bar */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/workplace-reset')}
          aria-label="Back"
          className="w-9 h-9 rounded-full bg-[#FFFCF3] border border-stone-200 flex items-center justify-center active:scale-95 transition-transform"
        >
          <ChevronLeft className="w-5 h-5 text-[#F8F6F0]" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#A3BEAF]">
          Round {currentRound} / {config.rounds}
        </p>
        <div className="w-9" aria-hidden />
      </div>

      {/* Title */}
      <div className="px-5 text-center mt-2">
        <h1 className="rbr-heading text-2xl mb-1">{config.title}</h1>
        <p className="text-[#C4D3C9] text-xs">{config.tagline}</p>
      </div>

      {/* Animation */}
      <div className="flex-1 flex items-center justify-center px-5">
        <Visual phaseIdx={phaseIdx} phaseProgress={phaseProgress} accent={config.accent} />
      </div>

      {/* Coached instruction */}
      <div className="px-5 pb-6 text-center">
        <p
          key={phaseIdx}
          className="text-lg font-semibold text-[#F8F6F0]"
          style={{
            fontFamily: "'Playfair Display', serif",
            animation: 'wpFadeIn 500ms ease-out both',
          }}
          data-testid="workplace-breath-phase-label"
        >
          {config.phases[phaseIdx].label}
        </p>
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#A3BEAF] mt-1">
          {Math.max(0, Math.ceil(phaseSeconds - phaseElapsed))}s
        </p>

        {/* Progress bar */}
        <div className="mt-5 mx-auto max-w-xs h-1.5 rounded-full bg-[#E8DDCB] overflow-hidden">
          <div
            className="h-full rounded-full transition-[width] duration-100"
            style={{ width: `${overallProgress * 100}%`, background: config.accent }}
          />
        </div>

        {/* Controls */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setRunning((r) => !r)}
            data-testid="workplace-breath-toggle"
            className="px-6 py-3 rounded-full text-xs font-semibold uppercase tracking-wide transition-all active:scale-95 flex items-center gap-2"
            style={{
              background: config.accent,
              color: '#F8F6F0',
              boxShadow: `0 6px 20px ${config.accent}55`,
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            {running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {t >= totalDuration ? 'Done' : running ? 'Pause' : 'Resume'}
          </button>
          {t > 0 && (
            <button
              type="button"
              onClick={() => { setT(0); setRunning(true); }}
              data-testid="workplace-breath-restart"
              className="px-4 py-3 rounded-full text-xs font-semibold uppercase tracking-wide bg-[#FFFCF3] border border-stone-200 text-[#F8F6F0] active:scale-95 transition-transform"
            >
              Restart
            </button>
          )}
        </div>

        {t >= totalDuration && (
          <p className="mt-5 text-sm text-[#C4D3C9]" data-testid="workplace-breath-complete">
            Nice reset. Back to it.
          </p>
        )}
      </div>

      <style>{`
        @keyframes wpFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WorkplaceBreathPage;
